import React, { createContext, useState } from "react";
import axios from "axios";
import jwt_decode from 'jwt-decode';
import { Outlet, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';

const AuthContext = createContext();

export default AuthContext;

function getTokens() {
    const raw = localStorage.getItem('authTokens');
    if (!raw) return null;
    try {
        return JSON.parse(raw);
    } catch {
        localStorage.removeItem('authTokens');
        return null;
    }
}

function getUserFromTokens(tokens) {
    if (!tokens?.access) return null;
    try {
        const data = jwt_decode(tokens.access);
        return { username: data.username };
    } catch {
        return null;
    }
}

export function AuthProvider() {

    const [authTokens, setAuthTokens] = useState(() => getTokens());
    const [user, setUser] = useState(() => getUserFromTokens(getTokens()));
    const navigate = useNavigate();

    function persistTokens(tokens) {
        setAuthTokens(tokens);
        setUser(getUserFromTokens(tokens));
        localStorage.setItem('authTokens', JSON.stringify(tokens));
    }

    async function loginUser(data) {
        try {
            const response = await axios.post(`${API_BASE_URL}/api/users/token/`, {
                username: data.username,
                password: data.password,
            });
            // 200 with a 2fa_required code = no tokens yet; caller must verify the code.
            if (response.status === 200 && response.data?.access) {
                persistTokens(response.data);
            }
            return response;
        } catch (err) {
            return err.response ?? err;
        }
    }

    async function verifyTwoFactor(preAuthToken, code) {
        try {
            const response = await axios.post(`${API_BASE_URL}/api/users/2fa/verify/`, {
                pre_auth_token: preAuthToken,
                code,
            });
            if (response.status === 200 && response.data?.access) {
                persistTokens(response.data);
            }
            return response;
        } catch (err) {
            return err.response ?? err;
        }
    }

    async function resendTwoFactor(preAuthToken) {
        try {
            return await axios.post(`${API_BASE_URL}/api/users/2fa/resend/`, {
                pre_auth_token: preAuthToken,
            });
        } catch (err) {
            return err.response ?? err;
        }
    }

    async function googleLogin(credential) {
        try {
            const response = await axios.post(`${API_BASE_URL}/api/users/google-auth/`, { credential });
            if (response.status === 200) {
                persistTokens(response.data);
            }
            return response;
        } catch (err) {
            return err.response ?? err;
        }
    }

    function acceptTokens(tokens) {
        // For flows that already have JWTs (e.g. email-verify success).
        persistTokens(tokens);
    }

    function logoutUser() {
        setAuthTokens(null);
        setUser(null);
        localStorage.removeItem('authTokens');
        navigate('/');
    }

    const contextData = {
        user,
        setUser,
        authTokens,
        setAuthTokens,
        loginUser,
        googleLogin,
        verifyTwoFactor,
        resendTwoFactor,
        acceptTokens,
        logoutUser,
    };

    return (
        <AuthContext.Provider value={contextData}>
            <Outlet />
        </AuthContext.Provider>
    );
}